import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DropResult,
} from '@hello-pangea/dnd';
import { transform } from 'ol/proj';
import { cn } from '@ais-ui/utils';
import {
  Button,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Icons,
  Label,
  Popover,
  PopoverDragNDrop,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  getIconFromLayerType,
  getTextFromLayerType,
  LayerItem,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import {
  PiMagnifyingGlassPlusThin,
  PiStack,
  PiStar,
  PiStarFill,
  PiTagFill,
  PiTagThin,
} from 'react-icons/pi';
import { Bookmarks } from './Actions/Bookmarks';
import { BaseMaps } from './Actions/BaseMaps';
import VectorLayer from 'ol/layer/Vector';
import { X } from 'lucide-react';
import { FilterBadge } from '../FilterTargets/FilterShips';
import { SimpleFilter, useFilterContext } from '../FilterContext';
import { useMapLegendSettings } from '../ColorTargetsBy/ColorBy/useMapLegendSettings';
import { Filter } from '@ais-ui/websockets';
import {
  isLayerFavorite,
  type LayerType as FavoriteLayerType,
} from '../onMapStartup/LayersOnStart/layerFavoriteCore';
import { useLayerFavorites } from '../onMapStartup/LayersOnStart/useLayerFavorites';
import { useSettings } from '../../Settings/SettingsProvider';
import { Coverage } from './Actions/Coverage';
import { GPSLost } from './Actions/GPSLost';
import { GPSLostActions } from './GPSLostActions';
import { CoverageActions } from './CoverageActions';
import { ABBWeatherActions } from './ABBWeatherActions';
import { ExternalLayers } from './Actions/ExternalLayers';
import { WeatherLegendToggle } from './WeatherLegendToggle';
import { useWeatherLegend } from '../WeatherLegend';
import { useCoverage } from '../Coverage/CoverageContext';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { GPSLostSelectedShips } from './GPSLostSelectedShips';
import OlGeometry from 'ol/geom/Geometry';
import Feature from 'ol/Feature';
import { AppearanceControl } from './AppearanceControl';
import { VectorTrackActions } from './VectorTrackActions';
import { NestedLayerRow } from './NestedLayerRow';
import { LayerRow } from './LayerRow';
import { calculateLayerControlContentHeight } from './calculateLayerControlContentHeight';
import {
  getTrackColorModeShortLabel,
  type TrackColorMode,
} from '@ais-ui/synthetic-geo';

const noop = () => {};

/** Map OL layer types to favorite-system LayerType. Returns null for non-favoritable layers. */
const getFavoriteLayerType = (olType: string): FavoriteLayerType | null => {
  switch (olType) {
    case 'wms':
      return 'wms';
    case 'vectortile':
      return 'vectortile';
    case 'openseamap':
      return 'openseamap';
    case 'cambridge-radar':
      return 'cambridge-radar';
    case 'coverage':
    case 'source-identifiers':
      return 'coverage';
    case 'circle':
    case 'point':
    case 'polygon':
    case 'linestring':
    case 'featurecollection':
      return 'shape';
    default:
      return null;
  }
};

// Helper function to parse WKT Point - same as in CoverageContext
const parsePoint = (wkt: string): [number, number] => {
  // Expected format: 'POINT (longitude latitude)'
  const match = wkt.match(
    /^POINT\s*\(\s*(-?\d+(\.\d+)?)\s+(-?\d+(\.\d+)?)\s*\)$/,
  );

  if (!match) {
    throw new Error(`Invalid WKT Point format: "${wkt}"`);
  }

  const longitude = parseFloat(match[1]);
  const latitude = parseFloat(match[3]);

  return [longitude, latitude];
};

interface LayerControlProps {
  className?: string;
}

interface DragNDropLayerProps {
  layer: LayerItem;
  index: number;
}

interface ShowLabelsButtonProps {
  layer: LayerItem;
  className?: string;
}

const ShowLabelsButton = React.forwardRef<
  HTMLButtonElement,
  ShowLabelsButtonProps
>(({ layer, className }, ref) => {
  const { toggleLabels } = useLayers();
  const [labelsAreActive, setLabelsAreActive] = useState(
    layer?.properties?.showLabel,
  );

  useEffect(() => {
    if (layer?.properties?.showLabel) {
      setLabelsAreActive(true);
    } else {
      setLabelsAreActive(false);
    }
  }, [layer]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          size="icon"
          variant="ghost"
          className={cn('size-9 flex-shrink-0 p-0 m-0', className)}
          onClick={() => {
            toggleLabels(layer);
          }}
        >
          {labelsAreActive ? (
            <PiTagFill className="size-5 text-primary" />
          ) : (
            <PiTagThin className="size-5" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Toggle labels</TooltipContent>
    </Tooltip>
  );
});

ShowLabelsButton.displayName = 'ShowLabelsButton';

const DragNDropLayer = React.forwardRef<HTMLDivElement, DragNDropLayerProps>(
  ({ layer, index }, _ref) => {
    const { messages } = useLocalization();
    const { displayProjection, dataProjection } = useOpenLayersContext();
    const {
      toggleLayerVisibility,
      removeLayer,
      zoomToLayer,
      zoomToExtent,
      getLayersByIdContaining,
    } = useLayers();

    const [disableDragNnDropTooltip, setDisableDragNnDropTooltip] =
      useState(false);
    const [justToggled, setJustToggled] = useState(false);

    const { settings } = useSettings();
    const { onDisableAll, onEnableAll } = useMapLegendSettings();
    const layerFavorites = useLayerFavorites();
    const { dismissLegendForLayer, timepointLabels } = useWeatherLegend();
    const { coverageDate } = useCoverage();
    const { instances, selectedInstanceIndex } = useGPSLost();
    const { formatDateTimeWithTimeZone } = useDateTimeFormat();

    const favoriteLayerType = useMemo(
      () => getFavoriteLayerType(layer.type),
      [layer.type],
    );

    const LayerIcon = useMemo(
      () => getIconFromLayerType(layer.type),
      [layer.type],
    );
    const layerTypeTooltip = useMemo(
      () => getTextFromLayerType(layer.type),
      [layer.type],
    );

    const isFeatureLayer = useMemo(() => {
      return (
        layer.type === 'circle' ||
        layer.type === 'point' ||
        layer.type === 'polygon' ||
        layer.type === 'linestring' ||
        layer.type === 'featurecollection'
      );
    }, [layer.type]);

    // Port admin layers (boundaries / terminals) are geometrically feature layers,
    // but they are not editable shapes — so they get the simple label (tag) toggle
    // instead of the shape-oriented appearance control.
    const isPortAdminLayer = useMemo(() => {
      return (
        layer.properties?.type === 'port-admin' ||
        layer.properties?.type === 'port-admin-inner'
      );
    }, [layer.properties?.type]);

    const isStartUpLayer = useMemo(() => {
      return isLayerFavorite(settings, layer.id);
    }, [settings, layer.id]);

    const labelMaxWidth = useMemo(() => {
      const ROW_WIDTH = 366;
      const BUTTON_SIZE = 36;
      const BUTTON_GAP = 4; // space-x-1
      const LEFT_FIXED = 56; // checkbox(16) + gap(16) + icon(16) + gap(8)

      let count = 0;
      if (layer.canZoomToFromLayerControl) count++;
      if ((!isFeatureLayer && layer.canShowLabel) || isFeatureLayer) count++;
      if (layer.type === 'gps-lost-grid') count++;
      if (layer.type === 'abb-weather') count++;
      if (favoriteLayerType) count++;
      if (layer.isRemovableFromLayerControl) count++;

      const buttonsWidth =
        count > 0 ? count * BUTTON_SIZE + (count - 1) * BUTTON_GAP : 0;

      return ROW_WIDTH - LEFT_FIXED - buttonsWidth;
    }, [layer, isFeatureLayer, favoriteLayerType]);

    const layerDateSubtitle = useMemo(() => {
      if (layer.type === 'coverage' && coverageDate?.from && coverageDate?.to) {
        const from = formatDateTimeWithTimeZone(coverageDate.from, true);
        const to = formatDateTimeWithTimeZone(coverageDate.to, true);
        return `${from} — ${to}`;
      }
      if (layer.type === 'gps-lost-grid' && instances.length > 0) {
        const instance = instances[selectedInstanceIndex];
        if (instance?.timerange?.lower && instance?.timerange?.upper) {
          const from = formatDateTimeWithTimeZone(
            instance.timerange.lower,
            true,
          );
          const to = formatDateTimeWithTimeZone(instance.timerange.upper, true);
          return `${from} — ${to}`;
        }
      }
      return null;
    }, [
      layer.type,
      coverageDate,
      instances,
      selectedInstanceIndex,
      formatDateTimeWithTimeZone,
    ]);

    const colorByLabel = useMemo(() => {
      if (layer.type !== 'vector-track') return null;
      const mode = layer.properties?.colorBy as TrackColorMode | undefined;
      if (!mode || mode === 'track') return null;
      return getTrackColorModeShortLabel(mode);
    }, [layer.type, layer.properties?.colorBy]);

    const onRemoveLayer = useCallback(
      (layer: LayerItem) => {
        removeLayer(layer.layer);
        if (layer.type === 'abb-weather') {
          const weatherId = layer.properties?.abbLayerId || layer.id;
          dismissLegendForLayer(weatherId);
        } else if (layer.type === 'coverage' && layer.properties?.id) {
          const event = new CustomEvent('removeCoverage', {
            detail: { id: layer.properties.id },
          });
          window.dispatchEvent(event);
        } else if (layer.type === 'gps-lost-grid') {
          const event = new CustomEvent('removeGPSLostGrid', {
            detail: { id: layer.properties.id },
          });

          const gpsLostLayers = getLayersByIdContaining('gps-lost-track');
          gpsLostLayers.forEach((layer) => {
            removeLayer(layer.layer);
          });

          window.dispatchEvent(event);
        } else if (
          layer.type === 'cambridge-radar' &&
          layer.properties?.radarId
        ) {
          const event = new CustomEvent('removeRadar', {
            detail: { radarId: layer.properties.radarId },
          });
          window.dispatchEvent(event);
        }
      },
      [removeLayer, getLayersByIdContaining, dismissLegendForLayer],
    );

    const onZoomToLayer = useCallback(
      (layer: LayerItem) => {
        if (layer.layer instanceof VectorLayer) {
          // Use zoomToLayer for vector layers
          zoomToLayer(layer.layer as VectorLayer<Feature<OlGeometry>>);
        } else if (
          layer.type === 'coverage' &&
          layer.properties?.sourceIdentifierPos
        ) {
          // Zoom to the center of the source identifier position
          const wktPos = layer.properties.sourceIdentifierPos;
          try {
            // Use the same parsePoint helper that's used elsewhere
            const [longitude, latitude] = parsePoint(wktPos);

            // Transform coordinates from EPSG:4326 to the map's projection
            const [x, y] = transform(
              [longitude, latitude],
              dataProjection,
              displayProjection,
            );

            // Create a small extent around the point for zooming
            const buffer = 15000;
            const extent = [x - buffer, y - buffer, x + buffer, y + buffer];

            zoomToExtent(extent);
          } catch (error) {
            console.warn(
              'Could not parse source identifier position:',
              wktPos,
              error,
            );
          }
        } else {
          console.warn(
            'Cannot zoom to layer. Unsupported layer type or missing data:',
            layer,
          );
        }
      },
      [zoomToLayer, zoomToExtent],
    );

    if (layer.isBase) {
      return;
    }

    if (layer.type === 'gps-lost-track-linestring') {
      return;
    }

    if (layer.type === 'gps-lost-track-point') {
      return;
    }

    return (
      <Draggable draggableId={layer.id} index={index}>
        {(provided, snapshot) => {
          const isDragging = snapshot.isDragging;

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  className={cn(
                    'flex items-center justify-between px-2 transition-all duration-200',
                    isDragging &&
                      'absolute transform scale-105 bg-muted shadow-sm rounded-md cursor-grabbing z-50',
                    !isDragging &&
                      'bg-card hover:bg-muted rounded-lg hover:cursor-move hover:shadow-sm',
                    justToggled && !isDragging && 'bg-primary/5',
                  )}
                >
                  <div className="w-full h-10 flex items-center justify-between relative">
                    <div className="flex items-center space-x-4 min-w-0">
                      <Checkbox
                        checked={layer.isVisible}
                        onCheckedChange={(value) => {
                          setJustToggled(true);
                          setTimeout(() => setJustToggled(false), 300);
                          if (layer.name.toLowerCase() === 'vessels') {
                            if (layer.isVisible) {
                              onDisableAll();
                            } else {
                              onEnableAll();
                            }
                          }
                          toggleLayerVisibility(layer.layer, value as boolean);
                        }}
                        id={layer.id}
                      />
                      <div className="flex items-center space-x-2 min-w-0">
                        <Tooltip>
                          <TooltipTrigger className="hover:cursor-default">
                            <div className="relative">
                              <LayerIcon className={cn('size-4')}></LayerIcon>
                              {isStartUpLayer && (
                                <PiStarFill className="absolute size-2 text-yellow-400 -top-1.5 -right-1.5" />
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>{layerTypeTooltip}</TooltipContent>
                        </Tooltip>
                        <div className="min-w-0">
                          <Label
                            style={{ maxWidth: labelMaxWidth }}
                            className={cn(
                              'text-sm font-medium cursor-move truncate block transition-opacity duration-200',
                              isDragging && 'cursor-grabbing',
                              !isDragging && 'hover:cursor-move',
                              !layer.isVisible && 'opacity-50',
                            )}
                            htmlFor={layer.id}
                          >
                            {layer.name}
                          </Label>
                          {layer.type === 'abb-weather' &&
                            layer.properties?.abbLayerId &&
                            timepointLabels[
                              layer.properties.abbLayerId as string
                            ] && (
                              <span className="text-[10px] text-muted-foreground truncate block">
                                {
                                  timepointLabels[
                                    layer.properties.abbLayerId as string
                                  ]
                                }
                              </span>
                            )}
                          {(layer.type === 'coverage' ||
                            layer.type === 'gps-lost-grid') &&
                            layerDateSubtitle && (
                              <span className="text-[10px] text-muted-foreground truncate block">
                                {layerDateSubtitle}
                              </span>
                            )}
                          {layer.type === 'vector-track' &&
                            (layer.metadata || colorByLabel) && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-[10px] text-muted-foreground truncate block cursor-default">
                                    {[layer.metadata, colorByLabel]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </span>
                                </TooltipTrigger>
                                {layer.properties?.lastUpdatedAt && (
                                  <TooltipContent>
                                    Updated{' '}
                                    {formatDateTimeWithTimeZone(
                                      new Date(
                                        layer.properties
                                          .lastUpdatedAt as string,
                                      ),
                                    )}
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )}
                        </div>
                      </div>
                      {/* Show GPS Lost inline actions during drag */}
                      {layer.type === 'gps-lost-grid' && (
                        <GPSLostActions
                          setDisableDragNnDropTooltip={
                            setDisableDragNnDropTooltip
                          }
                          isDragging={isDragging}
                          variant="inline"
                        />
                      )}
                      {/* Show Coverage inline actions during drag */}
                      {layer.type === 'coverage' && (
                        <CoverageActions
                          setDisableDragNnDropTooltip={
                            setDisableDragNnDropTooltip
                          }
                          isDragging={isDragging}
                          variant="inline"
                        />
                      )}
                      {/* Show ABB Weather inline actions during drag */}
                      {layer.type === 'abb-weather' && (
                        <ABBWeatherActions
                          layer={layer}
                          setDisableDragNnDropTooltip={
                            setDisableDragNnDropTooltip
                          }
                          isDragging={isDragging}
                          variant="inline"
                        />
                      )}
                    </div>
                    <div className="flex space-x-1 flex-shrink-0">
                      {layer.canZoomToFromLayerControl && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              id={`zoom-to-layer-${layer.id}`}
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 flex-shrink-0 p-0 m-0"
                              onClick={() => onZoomToLayer(layer)}
                            >
                              <PiMagnifyingGlassPlusThin className="size-5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Zoom to layer</TooltipContent>
                        </Tooltip>
                      )}
                      {((!isFeatureLayer && layer.canShowLabel) ||
                        isPortAdminLayer) && (
                        <ShowLabelsButton layer={layer}></ShowLabelsButton>
                      )}
                      {isFeatureLayer && !isPortAdminLayer && (
                        <AppearanceControl
                          layer={layer}
                          canToggleLabels={true}
                          canChangeColor={true}
                          canChangeTransparency={true}
                          canChangeSize={false}
                        ></AppearanceControl>
                      )}
                      {layer.type === 'gps-lost-grid' && (
                        <GPSLostSelectedShips></GPSLostSelectedShips>
                      )}
                      {layer.type === 'abb-weather' && (
                        <WeatherLegendToggle layer={layer} />
                      )}
                      {layer.type === 'vector-track' && (
                        <VectorTrackActions layer={layer} />
                      )}
                      {favoriteLayerType && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 flex-shrink-0 p-0 m-0"
                              disabled={layerFavorites.isToggling(layer.id)}
                              onClick={() =>
                                layerFavorites.toggleFavorite(
                                  layer.id,
                                  favoriteLayerType,
                                  layer.name,
                                )
                              }
                            >
                              {layerFavorites.isToggling(layer.id) ? (
                                <Icons.spinner className="size-4 animate-spin" />
                              ) : isStartUpLayer ? (
                                <PiStarFill className="size-4 text-yellow-400" />
                              ) : (
                                <PiStar className="size-4 text-muted-foreground" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {isStartUpLayer
                              ? 'Remove from startup'
                              : 'Show on startup'}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {layer.isRemovableFromLayerControl && (
                        <LayerRow.RemoveButton
                          id={`remove-layer-${layer.id}`}
                          onClick={() => onRemoveLayer(layer)}
                        />
                      )}
                    </div>
                    {layer.type === 'gps-lost-grid' && (
                      <GPSLostActions
                        setDisableDragNnDropTooltip={
                          setDisableDragNnDropTooltip
                        }
                        isDragging={isDragging}
                      />
                    )}
                    {layer.type === 'coverage' && (
                      <CoverageActions
                        setDisableDragNnDropTooltip={
                          setDisableDragNnDropTooltip
                        }
                        isDragging={isDragging}
                      />
                    )}
                    {layer.type === 'abb-weather' && (
                      <ABBWeatherActions
                        layer={layer}
                        setDisableDragNnDropTooltip={
                          setDisableDragNnDropTooltip
                        }
                        isDragging={isDragging}
                      />
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              {!disableDragNnDropTooltip && (
                <TooltipContent>
                  {messages.layerControlOnHoverDragNDropTooltip}
                </TooltipContent>
              )}
            </Tooltip>
          );
        }}
      </Draggable>
    );
  },
);

DragNDropLayer.displayName = 'DragNDropLayer';

interface FilterBadgesProps {
  allFilters: (Filter | SimpleFilter)[];
}

const FilterBadges = ({ allFilters }: FilterBadgesProps) => {
  return (
    <div className="grid grid-rows-2 grid-flow-col gap-x-1 pointer-events-auto">
      {allFilters.map((filter) => {
        const isFilter = (filter: Filter | SimpleFilter): filter is Filter => {
          return (filter as Filter).uuid !== undefined;
        };

        // Use the type guard to check the type of filter
        const key = isFilter(filter) ? filter.uuid : filter.id;

        return <FilterBadge key={key} filter={filter} />;
      })}
    </div>
  );
};

export const LayerControl = React.memo(
  React.forwardRef<HTMLDivElement, LayerControlProps>(({ className }, _ref) => {
    const { messages } = useLocalization();
    const [open, setOpen] = useState(false);
    const { layers, rearrangeLayer } = useLayers();

    const filterContext = useFilterContext();

    const allFilters = useMemo(
      () => [...filterContext.simpleFilters, ...filterContext.customFilters],
      [filterContext.simpleFilters, filterContext.customFilters],
    );

    const onDragEnd = useCallback(
      (result: DropResult) => {
        if (!result.destination) return;

        const sourceIndex = layers.length - result.source.index - 1;
        const destinationIndex = layers.length - result.destination.index - 1;
        const movedLayer = layers[sourceIndex];

        rearrangeLayer(movedLayer, destinationIndex);
      },
      [layers, rearrangeLayer],
    );

    const handleToggle = useCallback(() => {
      setOpen(!open);
    }, [open]);

    const handleClose = useCallback(() => {
      setOpen(false);
    }, []);

    const reversedLayers = useMemo(() => {
      // All top-level layers participate in ordering (including those with children)
      return layers.slice().reverse();
    }, [layers]);

    const visibleLayers = useMemo(() => {
      return layers.filter((layer) => {
        if (layer.isBase) return false;
        if (layer.type === 'gps-lost-track-linestring') return false;
        if (layer.type === 'gps-lost-track-point') return false;
        return true;
      });
    }, [layers]);

    // Cheap to compute (a small reduce), and only used for an inline style —
    // no useMemo needed. Deriving it directly during render also avoids the
    // stale-dependency-array bug this used to have.
    const contentHeight = calculateLayerControlContentHeight({
      layers: visibleLayers,
    });

    return (
      <div className="flex space-x-2 h-14 h-max-14">
        <Popover open={open} onOpenChange={noop}>
          <div className="relative flex space-x-2">
            <Tooltip>
              <PopoverTrigger asChild>
                <TooltipTrigger asChild>
                  <Button
                    id="layer-control-button"
                    onClick={handleToggle}
                    variant="outline"
                    size="icon"
                    className={cn(
                      'flex self-end items-center justify-center h-14 w-14 p-2 group/btn rounded-lg',
                      className,
                    )}
                  >
                    <PiStack className="size-9 group-hover/btn:text-primary" />
                  </Button>
                </TooltipTrigger>
              </PopoverTrigger>
              <TooltipContent side="right" sideOffset={4}>
                {messages.mapLayerControlButtonTooltip}
              </TooltipContent>
            </Tooltip>
            {open && (
              <PopoverDragNDrop
                className={cn(
                  'absolute bottom-0 left-16 px-2 pt-4 pb-2 w-[424px]',
                  className,
                )}
              >
                <CardHeader className="p-2 space-y-6">
                  <div>
                    <div className="flex justify-between">
                      <CardTitle className="text-lg font-medium pb-2">
                        Actions
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="-mt-5 -mr-3"
                        onClick={handleClose}
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                    <Separator />
                    <div className="flex space-x-4 pt-4">
                      <Bookmarks />
                      <BaseMaps />
                      <ExternalLayers />
                      <Coverage />
                      <GPSLost />
                    </div>
                  </div>
                  <div>
                    <CardTitle className="text-lg font-medium">
                      {messages.layerControlTitle}
                    </CardTitle>
                    <Separator className="mt-2" />
                  </div>
                </CardHeader>
                <CardContent
                  className="max-h-96 p-2 pt-0"
                  style={{ height: contentHeight }}
                >
                  <ScrollArea className="h-full">
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="layers" type="task">
                        {(provided: DroppableProvided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="grid gap-2 mr-2 -m-1 p-1"
                          >
                            {reversedLayers.map((layer, index) =>
                              layer.children ? (
                                <Draggable
                                  key={layer.id}
                                  draggableId={layer.id}
                                  index={index}
                                >
                                  {(dragProvided, dragSnapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                      className={cn(
                                        'transition-all duration-200',
                                        dragSnapshot.isDragging
                                          ? 'transform scale-105 bg-muted shadow-sm rounded-md cursor-grabbing z-50'
                                          : 'bg-card rounded-lg hover:cursor-move hover:shadow-sm',
                                      )}
                                    >
                                      <NestedLayerRow layer={layer} />
                                    </div>
                                  )}
                                </Draggable>
                              ) : (
                                <DragNDropLayer
                                  key={layer.id}
                                  layer={layer}
                                  index={index}
                                />
                              ),
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </ScrollArea>
                </CardContent>
              </PopoverDragNDrop>
            )}
          </div>
        </Popover>
        {!open && allFilters.length > 0 && (
          <FilterBadges allFilters={allFilters} />
        )}
      </div>
    );
  }),
);

LayerControl.displayName = 'LayerControl';
